import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { WHATSAPP_URL } from "../lib/constants";
import { usePageMeta } from "../lib/usePageMeta";
import {
  Flower2, Moon, Sparkles, Heart, Sunrise, Waves, ShieldCheck, FileCheck2,
  ClipboardList, Award, ArrowRight, Feather, Compass, Users,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?auto=format&fit=crop&w=1600&q=70";
const CIRCLE_IMG = "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/dd046ra1_WhatsApp%20Image%202026-09-19%20at%2000.47.23.jpeg";
const NATURE_IMG = "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/30yhnt5u_WhatsApp%20Image%202026-09-19%20at%2000.47.24.jpeg";
const CTA_IMG = "https://images.unsplash.com/photo-1518604666860-9ed391f76460?auto=format&fit=crop&w=1600&q=70";

const PILLARS = [
  {
    icon: Sparkles,
    title: "Autoconhecimento profundo",
    body: "Círculos de escuta, escrita reflexiva e práticas de respiração conduzidas por facilitadoras certificadas em coaching sistêmico e constelação familiar.",
  },
  {
    icon: Heart,
    title: "Cura da alma",
    body: "Vivências terapêuticas com músicas medicinais, cerimônias de agradecimento respeitando culturas ancestrais e rodas de partilha em grupo pequeno.",
  },
  {
    icon: Moon,
    title: "Sincronização de energia e karma",
    body: "Meditações guiadas, banhos de floresta (shinrin-yoku), rituais de lua cheia à beira-mar e práticas de liberação emocional acompanhadas.",
  },
  {
    icon: Waves,
    title: "Destinos que fazem sentir viva",
    body: "Chapada Diamantina, Ilha Grande, Fernando de Noronha, Bali e Sedona: lugares escolhidos pelo poder de reconexão que devolvem à mulher o próprio eixo.",
  },
  {
    icon: Feather,
    title: "Feminino sagrado",
    body: "Grupos exclusivos de mulheres com hospedagem só-mulheres, temas do sagrado feminino, arquétipos e uma cerimônia de encerramento inesquecível.",
  },
  {
    icon: Sunrise,
    title: "Renovação real",
    body: "Cada retiro entrega uma mulher diferente da que embarcou: mais leve, mais lúcida, com propósito redesenhado — não é frase de folder, é o padrão da Orion.",
  },
];

const AUDIENCES = [
  { title: "Mulheres em Transição", body: "Divórcio, mudança de carreira, luto, filhos que saem de casa — momentos que pedem pausa consciente." },
  { title: "Grupos de Amigas", body: "Rodas fechadas de 6 a 12 mulheres com curadoria feita pra fortalecer laços e criar memória afetiva." },
  { title: "Terapeutas & Facilitadoras", body: "Estruturamos toda a logística do seu retiro autoral — você foca no conteúdo, a Orion cuida do resto." },
  { title: "Empresas Femininas", body: "Off-sites de equipes lideradas por mulheres com bem-estar corporativo e liderança consciente." },
];

const TRUST = [
  { icon: ShieldCheck, title: "Cadastur ativo", body: "Ministério do Turismo." },
  { icon: FileCheck2, title: "Contrato formal", body: "Cláusulas alinhadas ao CDC." },
  { icon: ClipboardList, title: "Nota fiscal", body: "Emissão sobre serviço prestado." },
  { icon: Award, title: "Facilitadoras certificadas", body: "Coaching, yoga, constelação." },
];

export default function ViagensParaMulheres() {
  usePageMeta(
    "Viagens para Mulheres · Retiros de Autoconhecimento — Viagens Orion",
    "Retiros exclusivos para mulheres: autoconhecimento, cura da alma, sincronização de energia e karma. Grupos pequenos, facilitadoras certificadas, contrato e Cadastur ativo. Destinos que reconectam ao próprio eixo."
  );

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1" data-testid="mulheres-page">
        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#4a1f3d] via-[#2C1139] to-[#0d0413] text-white">
          <span className="absolute inset-0 opacity-30" aria-hidden style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }} />
          <span className="absolute inset-0 bg-gradient-to-r from-[#2C1139]/95 via-[#2C1139]/60 to-transparent" aria-hidden />
          <span className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#FF9EC1]/25 blur-3xl" aria-hidden />

          <div className="relative container-orion py-16 md:py-28 max-w-6xl">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-5">
              <Flower2 size={13} /> Viagens Orion · Feminino
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.02] max-w-4xl">
              Viagens para <span className="text-orion-gold">mulheres</span> que decidiram viver de verdade.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
              Retiros exclusivos para grupos pequenos: autoconhecimento profundo, cura da
              alma, sincronização de energia e karma. Facilitadoras certificadas, hospedagem
              só-mulheres — com contrato, NF e Cadastur.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-orion-gold text-orion-purple px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#c19a3f] transition-all shadow-[0_14px_38px_rgba(216,167,73,0.45)] hover:-translate-y-0.5"
                data-testid="mulheres-cta-wa"
              >
                <WhatsAppIcon size={16} color="#2C1139" /> Falar com uma Facilitadora
              </a>
              <a href="#experiencias" className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:border-orion-gold hover:text-orion-gold transition-colors">
                Ver Experiências <ArrowRight size={14} />
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-4 md:gap-8 text-[10px] uppercase tracking-[0.3em] text-white/75">
              <span className="inline-flex items-center gap-2"><ShieldCheck size={12} className="text-orion-gold" /> Cadastur ativo</span>
              <span className="inline-flex items-center gap-2"><FileCheck2 size={12} className="text-orion-gold" /> Contrato formal</span>
              <span className="inline-flex items-center gap-2"><ClipboardList size={12} className="text-orion-gold" /> Nota fiscal</span>
              <span className="inline-flex items-center gap-2"><Award size={12} className="text-orion-gold" /> Grupos de até 12</span>
            </div>
          </div>
        </section>

        {/* TRUST STRIP */}
        <section className="border-y border-[#EFEAE0] bg-[#FAF7F0]">
          <div className="container-orion py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {TRUST.map((t, i) => (
              <div key={i} className="flex flex-col items-center gap-2" data-testid={`mulheres-trust-${i}`}>
                <t.icon size={22} className="text-orion-orange" />
                <div className="font-serif text-orion-purple text-sm md:text-base">{t.title}</div>
                <div className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-[#594A63] leading-snug">{t.body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* PILLARS */}
        <section id="experiencias" className="container-orion py-20 md:py-28">
          <div className="max-w-2xl mb-14">
            <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Nossas Vertentes</div>
            <h2 className="font-serif text-3xl md:text-5xl text-orion-purple leading-tight">
              Uma experiência para cada camada da alma.
            </h2>
            <p className="mt-4 text-[#594A63] text-base md:text-lg leading-relaxed">
              Não é passeio. É travessia. Cada roteiro é co-criado com facilitadoras
              certificadas para atravessar as fases que só a gente sente, e nunca
              consegue explicar direito.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <div
                key={i}
                className="group border border-[#EFEAE0] p-7 hover:border-orion-orange hover:shadow-[0_18px_40px_rgba(44,17,57,0.12)] transition-all bg-white"
                data-testid={`mulheres-pillar-${i}`}
              >
                <p.icon size={30} className="text-orion-orange mb-4 group-hover:scale-110 transition-transform" strokeWidth={1.6} />
                <h3 className="font-serif text-xl text-orion-purple mb-3 leading-snug">{p.title}</h3>
                <p className="text-sm text-[#594A63] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* PHILOSOPHY */}
        <section className="bg-[#3d1b52] text-white overflow-hidden">
          <div className="container-orion py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
            <div className="grid grid-cols-2 gap-4 order-2 lg:order-1">
              <img src={CIRCLE_IMG} alt="Grupo de mulheres em vivência espiritual em Peruíbe" loading="lazy" className="w-full aspect-[3/4] object-cover rounded-lg shadow-[0_18px_40px_rgba(0,0,0,0.35)] mt-8" />
              <img src={NATURE_IMG} alt="Roda de meditação em vivência feminina em Peruíbe" loading="lazy" className="w-full aspect-[3/4] object-cover rounded-lg shadow-[0_18px_40px_rgba(0,0,0,0.35)]" />
            </div>
            <div className="order-1 lg:order-2">
              <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Nossa Filosofia</div>
              <h2 className="font-serif text-3xl md:text-5xl leading-[1.05]">
                A viagem começa antes do embarque.
              </h2>
              <p className="mt-5 text-white/85 text-base md:text-lg leading-relaxed">
                Cada retiro começa com uma <strong>reunião de preparação individual</strong> —
                porque o efeito de uma vivência bem conduzida se estende por meses depois.
                Trabalhamos com facilitadoras capacitadas em Coaching Sistêmico,
                Constelação Familiar, Yoga (RYT-200+) e Terapias Integrativas.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-white/85">
                {[
                  "Reunião pré-viagem individual — 1:1 com facilitadora",
                  "Material de apoio: diário guiado, playlist de meditação, arquétipo pessoal",
                  "Roda de fechamento presencial + follow-up 15 dias após a viagem",
                  "Respeito e valorização de culturas ancestrais (turismo comunitário)",
                  "Alerta médico prévio quando envolve cerimônias com plantas sagradas",
                ].map((li, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-orion-gold mt-1">›</span> {li}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* AUDIENCES */}
        <section className="container-orion py-20 md:py-28">
          <div className="max-w-2xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Para quem</div>
            <h2 className="font-serif text-3xl md:text-5xl text-orion-purple leading-tight">
              Feito para mulheres que sentem o chamado.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {AUDIENCES.map((a, i) => (
              <div key={i} className="p-7 border-t-4 border-orion-orange bg-[#FAF7F0]" data-testid={`mulheres-audience-${i}`}>
                <Users size={26} className="text-orion-orange mb-3" strokeWidth={1.6} />
                <h3 className="font-serif text-xl text-orion-purple mb-2 leading-snug">{a.title}</h3>
                <p className="text-sm text-[#594A63] leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="relative bg-gradient-to-br from-orion-gold via-[#c19a3f] to-[#8a6d24] text-white overflow-hidden">
          <span className="absolute inset-0 opacity-25" aria-hidden style={{ backgroundImage: `url(${CTA_IMG})`, backgroundSize: "cover" }} />
          <span className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" aria-hidden />
          <div className="relative container-orion py-20 md:py-28 text-center max-w-3xl">
            <Flower2 size={30} className="mx-auto text-white mb-5" />
            <h2 className="font-serif text-3xl md:text-5xl leading-tight">
              Vamos desenhar o próximo capítulo da sua vida?
            </h2>
            <p className="mt-5 text-white/95 text-base md:text-lg">
              Uma facilitadora da Orion responde em minutos. Compartilhe o momento que
              você está vivendo — devolvemos a proposta terapêutica + logística completa.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-white text-orion-purple px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-orion-cream transition-all hover:-translate-y-0.5 shadow-[0_14px_38px_rgba(0,0,0,0.25)]"
                data-testid="mulheres-cta-bottom"
              >
                <WhatsAppIcon size={16} color="#2C1139" /> Falar com Facilitadora
              </a>
              <Link
                to="/sobre"
                className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-white/10 transition-colors"
              >
                Conhecer a Orion
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
